import OryCompletenessReport from "./ory/OryCompletenessReport.jsx";
import ActionableSummaryPanel from "./remediation/ActionableSummaryPanel.jsx";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  LabelList,
} from "recharts";
import FRAMEWORKS from "../compliance/frameworks.js";
import { getDefaultProviderComplianceFrameworks } from "../utils/providerFrameworks.js";
import {
  computePostureScore,
  buildSeverityCounts,
  buildComplianceByFramework,
  buildRiskBySurface,
  summarizeFindingDrift,
  gaugeScoreColor,
  buildScoreCaption,
  computeScoreDelta,
  buildTrendSeries,
} from "../utils/postureScore.js";
import {
  EXEC_DASH_RING_SIZE,
  EXEC_DASH_RING_STROKE,
  EXEC_DASH_RING_R,
  EXEC_DASH_RING_CX,
  EXEC_DASH_RING_CY,
} from "../constants/execDashRingGeometry.js";

const DONUT_COLORS = {
  critical: "var(--critical)",
  high: "var(--high)",
  medium: "var(--medium)",
  low: "var(--low)",
  healthy: "var(--healthy)",
  na: "var(--na)",
  unassessed: "var(--na)",
};

const EMPTY_RISK_SURFACES = [
  { surface: "Identity", riskWeight: 0 },
  { surface: "Applications", riskWeight: 0 },
  { surface: "Admin & Config", riskWeight: 0 },
];

const DONUT_LEGEND = [
  { key: "critical", label: "Critical", color: DONUT_COLORS.critical },
  { key: "high", label: "High", color: DONUT_COLORS.high },
  { key: "medium", label: "Medium", color: DONUT_COLORS.medium },
  { key: "low", label: "Low", color: DONUT_COLORS.low },
  { key: "healthy", label: "Healthy", color: DONUT_COLORS.healthy },
  { key: "na", label: "N/A", color: DONUT_COLORS.na },
];

const TREND_Y_TICKS = [0, 50, 100];
const TREND_X_MAX_TICKS = 6;

const AXIS_TICK = { fontSize: 10.5, fill: "var(--text-faint)" };
const TOOLTIP_STYLE = {
  background: "var(--bg-inset)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  fontSize: 11,
  color: "var(--text-strong)",
};

function emptyComplianceRows(providerId) {
  const fws = getDefaultProviderComplianceFrameworks(providerId);
  return fws.map((framework) => ({ framework, covered: 0, partial: 0, failing: 0 }));
}

function frameworkColumnLabel(framework) {
  const def = FRAMEWORKS?.[framework];
  return def?.displayName || def?.name || String(framework);
}

function gaugeStrokeDash(score) {
  const circumference = 2 * Math.PI * EXEC_DASH_RING_R;
  const safe = Number.isFinite(Number(score)) ? Math.max(0, Math.min(100, Number(score))) : 0;
  const filled = circumference * (safe / 100);
  return { circumference, filled, gap: circumference - filled, safe };
}

function buildDonutArcs(segments, total) {
  const circumference = 2 * Math.PI * EXEC_DASH_RING_R;
  if (!total || total <= 0) return [];
  let offset = 0;
  return segments.map((seg) => {
    const len = (seg.value / total) * circumference;
    const arc = {
      key: seg.key,
      color: DONUT_COLORS[seg.key] || "var(--na)",
      dasharray: `${len} ${circumference - len}`,
      dashoffset: -offset,
    };
    offset += len;
    return arc;
  });
}

function enrichTrendSeries(trendSeries, snapshots) {
  const snaps = Array.isArray(snapshots) ? [...snapshots].reverse() : [];
  return (Array.isArray(trendSeries) ? trendSeries : []).map((point, i) => ({
    ...point,
    takenAt:
      point.date === "Now"
        ? new Date().toISOString()
        : snaps[i]?.takenAt ?? null,
  }));
}

function trendPointTime(point, index) {
  if (point?.takenAt) {
    const t = new Date(point.takenAt).getTime();
    if (Number.isFinite(t)) return t;
  }
  if (point?.date === "Now") return Date.now();
  const parsed = Date.parse(String(point?.date || ""));
  if (Number.isFinite(parsed)) return parsed;
  return index;
}

function formatTrendAxisLabel(point) {
  const t = point?.takenAt ? new Date(point.takenAt) : null;
  if (t && !Number.isNaN(t.getTime())) {
    return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  if (point?.date === "Now") {
    return new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  const raw = String(point?.date || "");
  const dayOnly = raw.match(/^([A-Za-z]{3}\s+\d{1,2})(?:\s|$)/);
  if (dayOnly) return dayOnly[1];
  return raw;
}

/** ~6 evenly-spaced tick indices by calendar time, not snapshot index */
function pickDateEvenTickIndices(series, maxTicks = TREND_X_MAX_TICKS) {
  if (!series.length) return new Set();
  if (series.length <= maxTicks) return new Set(series.map((_, i) => i));

  const timed = series.map((p, i) => ({ i, t: trendPointTime(p, i) }));
  const minT = timed[0].t;
  const maxT = timed[timed.length - 1].t;
  if (minT === maxT) return new Set([0]);

  const picked = new Set();
  for (let k = 0; k < maxTicks; k += 1) {
    const target = minT + (k / (maxTicks - 1)) * (maxT - minT);
    let best = timed[0].i;
    let bestDist = Infinity;
    for (const row of timed) {
      if (picked.has(row.i)) continue;
      const dist = Math.abs(row.t - target);
      if (dist < bestDist) {
        bestDist = dist;
        best = row.i;
      }
    }
    picked.add(best);
  }
  return picked;
}

function riskAxisMax(data) {
  const values = (Array.isArray(data) ? data : []).map((d) => Number(d?.riskWeight) || 0);
  const peak = values.length ? Math.max(...values) : 0;
  if (peak <= 0) return 10;
  const padded = Math.ceil(peak * 1.05);
  const rounded = Math.ceil(padded / 4) * 4;
  return Math.max(rounded, 8);
}

function riskAxisTicks(max) {
  const step = max / 4;
  return [0, step, step * 2, step * 3, max].map((v) => Math.round(v));
}

const RING_TRACK = "var(--exec-dash-ring-track)";

function ExecDashRingFrame({ svgChildren, overlay, className = "", ...frameProps }) {
  return (
    <div className={`exec-dash-ring-frame${className ? ` ${className}` : ""}`} {...frameProps}>
      <svg className="exec-dash-ring-svg" viewBox={`0 0 ${EXEC_DASH_RING_SIZE} ${EXEC_DASH_RING_SIZE}`} aria-hidden>
        <circle
          cx={EXEC_DASH_RING_CX}
          cy={EXEC_DASH_RING_CY}
          r={EXEC_DASH_RING_R}
          fill="none"
          stroke={RING_TRACK}
          strokeWidth={EXEC_DASH_RING_STROKE}
        />
        {svgChildren}
      </svg>
      {overlay}
    </div>
  );
}

function TrendArrow({ delta, hasPrior }) {
  if (!hasPrior) return null;
  if (delta === 0) {
    return (
      <span className="exec-dash-mono" style={{ color: "var(--text-faint)", fontSize: 11 }}>
        → no change
      </span>
    );
  }
  const up = delta > 0;
  const color = up ? "var(--healthy)" : "var(--critical)";
  return (
    <span className="exec-dash-mono" style={{ color, fontSize: 11, fontWeight: 600 }}>
      {up ? "▲" : "▼"} {Math.abs(delta)} vs prior
    </span>
  );
}

function HeroGauge({ score }) {
  const { filled, gap, safe } = gaugeStrokeDash(score);
  const color = gaugeScoreColor(safe);

  return (
    <ExecDashRingFrame
      overlay={
        <div className="exec-dash-ring-num">
          <span className="exec-dash-ring-score exec-dash-mono" style={{ color }}>
            {safe}
          </span>
        </div>
      }
      svgChildren={
        filled > 0 ? (
          <circle
            cx={EXEC_DASH_RING_CX}
            cy={EXEC_DASH_RING_CY}
            r={EXEC_DASH_RING_R}
            fill="none"
            stroke={color}
            strokeWidth={EXEC_DASH_RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${gap}`}
            transform={`rotate(-90 ${EXEC_DASH_RING_CX} ${EXEC_DASH_RING_CY})`}
          />
        ) : null
      }
    />
  );
}

function DonutWithLegend({ severity }) {
  const { counts, total, segments } = severity;
  const centerValue = counts.critical > 0 ? counts.critical : total;
  const centerLabel = counts.critical > 0 ? "critical" : "findings";
  const arcs = buildDonutArcs(segments, total);

  return (
    <div className="exec-dash-donut-hover" tabIndex={0} aria-label="Severity breakdown — hover for legend">
      <ExecDashRingFrame
        overlay={
          <div className="exec-dash-donut-center">
            <span className="exec-dash-donut-center__value exec-dash-mono">{centerValue}</span>
            <span className="exec-dash-donut-center__label">{centerLabel}</span>
          </div>
        }
        svgChildren={arcs.map((arc) => (
          <circle
            key={arc.key}
            cx={EXEC_DASH_RING_CX}
            cy={EXEC_DASH_RING_CY}
            r={EXEC_DASH_RING_R}
            fill="none"
            stroke={arc.color}
            strokeWidth={EXEC_DASH_RING_STROKE}
            strokeDasharray={arc.dasharray}
            strokeDashoffset={arc.dashoffset}
            transform={`rotate(-90 ${EXEC_DASH_RING_CX} ${EXEC_DASH_RING_CY})`}
          />
        ))}
      />
      <div className="exec-dash-donut-legend-popup" role="tooltip">
        {DONUT_LEGEND.map(({ key, label, color }) => (
          <div key={key} className="exec-dash-donut-legend-row">
            <span className="exec-dash-donut-legend-row__dot" style={{ background: color }} />
            <span className="exec-dash-donut-legend-row__lab">{label}</span>
            <span className="exec-dash-donut-legend-row__count exec-dash-mono">{counts[key] ?? 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GaugeDonutCluster({ score, delta, hasPrior, scoreCaption, severity }) {
  return (
    <div className="exec-dash-posture-cluster">
      <div className="exec-dash-posture-rings">
        <HeroGauge score={score} />
        <DonutWithLegend severity={severity} />
      </div>
      <div className="exec-dash-posture-footer">
        <div className="exec-dash-posture-footer__label">Posture score</div>
        {scoreCaption ? (
          <div className="exec-dash-posture-footer__caption">{scoreCaption}</div>
        ) : null}
        <div className="exec-dash-posture-footer__delta">
          <TrendArrow delta={delta} hasPrior={hasPrior} />
        </div>
      </div>
    </div>
  );
}

function ComplianceSymbolLegend() {
  return (
    <div className="exec-dash-compliance-legend">
      <span className="exec-dash-compliance-legend__item" style={{ color: "var(--healthy)" }}>
        ✓ covered
      </span>
      <span className="exec-dash-compliance-legend__item" style={{ color: "var(--warning)" }}>
        ~ partial
      </span>
      <span className="exec-dash-compliance-legend__item" style={{ color: "var(--critical)" }}>
        ✗ failing
      </span>
    </div>
  );
}

function ComplianceVerticalChart({ rows }) {
  const cols = Array.isArray(rows) && rows.length > 0 ? rows : [];

  return (
    <div className="exec-dash-hero-cell__body exec-dash-hero-cell__body--compliance">
      <ComplianceSymbolLegend />
      <div className="exec-dash-chart-zone">
        <div className="exec-dash-compliance-cols">
          {cols.map((row) => {
            const total = row.covered + row.partial + row.failing;
            const coveredH = total > 0 ? (row.covered / total) * 100 : 0;
            const partialH = total > 0 ? (row.partial / total) * 100 : 0;
            const failingH = total > 0 ? (row.failing / total) * 100 : 0;

            return (
              <div key={row.framework} className="exec-dash-compliance-col">
                <div className="exec-dash-compliance-counts exec-dash-mono">
                  <span style={{ color: "var(--healthy)" }}>{row.covered}✓</span>
                  <span style={{ color: "var(--warning)" }}>{row.partial}~</span>
                  <span style={{ color: "var(--critical)" }}>{row.failing}✗</span>
                </div>
                <div className="exec-dash-compliance-stack-wrap">
                  {total === 0 ? (
                    <div className="exec-dash-compliance-stack__empty" />
                  ) : (
                    <div className="exec-dash-compliance-stack">
                      {failingH > 0 ? (
                        <div style={{ flex: `${failingH} 0 0`, background: "var(--critical)", minHeight: 2 }} />
                      ) : null}
                      {partialH > 0 ? (
                        <div style={{ flex: `${partialH} 0 0`, background: "var(--warning)", minHeight: 2 }} />
                      ) : null}
                      {coveredH > 0 ? (
                        <div style={{ flex: `${coveredH} 0 0`, background: "var(--healthy)", minHeight: 2 }} />
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="exec-dash-compliance-labels">
        {cols.map((row) => {
          const name = frameworkColumnLabel(row.framework);
          return (
            <div key={`${row.framework}-label`} className="exec-dash-compliance-fw-label" title={name}>
              {name}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RiskValueLabel(props) {
  const { x = 0, y = 0, width = 0, height = 0, value } = props;
  return (
    <text
      x={x + width + 8}
      y={y + height / 2}
      fill="var(--text-muted)"
      fontSize={11}
      dominantBaseline="middle"
      className="exec-dash-mono"
    >
      {value}
    </text>
  );
}

function RiskBySurfaceChart({ data }) {
  const chartData = Array.isArray(data) && data.length > 0 ? data : EMPTY_RISK_SURFACES;
  const xMax = riskAxisMax(chartData);
  const xTicks = riskAxisTicks(xMax);

  return (
    <div className="exec-dash-hero-cell__body exec-dash-hero-cell__body--risk">
      <div className="exec-dash-risk-inner">
        <div className="exec-dash-risk-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 0, right: 36, top: 0, bottom: 16 }}
              barCategoryGap="22%"
            >
              <XAxis
                type="number"
                domain={[0, xMax]}
                ticks={xTicks}
                tick={AXIS_TICK}
                axisLine={{ stroke: "var(--border-soft)" }}
                tickLine={{ stroke: "var(--border-soft)" }}
                height={20}
              />
              <YAxis
                type="category"
                dataKey="surface"
                width={112}
                tick={{ fontSize: 12.5, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="riskWeight" fill="var(--warning)" radius={[0, 4, 4, 0]} barSize={16}>
                <LabelList dataKey="riskWeight" content={RiskValueLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function PostureTrendChart({ trendSeries, snapshots }) {
  const series = enrichTrendSeries(trendSeries, snapshots);
  if (!series.length) return null;

  const tickIndices = pickDateEvenTickIndices(series);
  const tickFormatter = (_value, index) =>
    tickIndices.has(index) ? formatTrendAxisLabel(series[index]) : "";

  return (
    <div className="exec-dash-trend-card">
      <h3 className="exec-dash-trend-card__title">Posture trend</h3>
      <div className="exec-dash-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 6, right: 16, left: 6, bottom: 4 }}>
            <defs>
              <linearGradient id="execDashTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2FC9AB" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#2FC9AB" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={AXIS_TICK}
              axisLine={{ stroke: "var(--border-soft)" }}
              tickLine={false}
              interval={0}
              tickMargin={8}
              tickFormatter={tickFormatter}
              padding={{ left: 12, right: 12 }}
            />
            <YAxis
              domain={[0, 100]}
              ticks={TREND_Y_TICKS}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={{ stroke: "var(--border-soft)" }}
              width={36}
              tickMargin={4}
            />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Area
              type="monotone"
              dataKey="score"
              stroke="none"
              fill="url(#execDashTrendFill)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#2FC9AB"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: "#2FC9AB" }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DashboardHeroRow({
  score,
  delta,
  hasPrior,
  scoreCaption,
  severity,
  complianceRows,
  riskBySurface,
}) {
  return (
    <div className="exec-dash-hero-row">
      <div className="exec-dash-hero-cell">
        <div className="exec-dash-hero-cell__title">Posture & severity</div>
        <div className="exec-dash-hero-cell__body exec-dash-hero-cell__body--cluster">
          <GaugeDonutCluster
            score={score}
            delta={delta}
            hasPrior={hasPrior}
            scoreCaption={scoreCaption}
            severity={severity}
          />
        </div>
      </div>

      <div className="exec-dash-hero-cell">
        <div className="exec-dash-hero-cell__title">Compliance coverage</div>
        <ComplianceVerticalChart rows={complianceRows} />
      </div>

      <div className="exec-dash-hero-cell exec-dash-hero-cell--risk">
        <div className="exec-dash-hero-cell__title">Risk by surface</div>
        <RiskBySurfaceChart data={riskBySurface} />
      </div>
    </div>
  );
}

function WhatChangedStrip({ driftSummary, driftChanges, inputChangeSummary, inputsChanged }) {
  const { newCritical, newHigh, resolved, items } = driftSummary || {};
  const hasItems = Array.isArray(items) && items.length > 0;
  const hasDriftChanges = Array.isArray(driftChanges) && driftChanges.length > 0;
  const hasInputChanges = Array.isArray(inputChangeSummary) && inputChangeSummary.length > 0;
  if (!newCritical && !newHigh && !resolved && !hasItems && !hasDriftChanges && !hasInputChanges && !inputsChanged) {
    return (
      <div style={{ fontSize: 11, color: "var(--text-faint)", padding: "4px 0" }}>
        No severity-meaningful changes since prior assessment.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      {hasInputChanges
        ? inputChangeSummary.slice(0, 4).map((line) => (
            <span
              key={line}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 999,
                background: "color-mix(in srgb, var(--accent, #38bdf8) 18%, transparent)",
                color: "var(--accent, #38bdf8)",
              }}
            >
              ↻ {line}
            </span>
          ))
        : null}
      {inputsChanged && !hasInputChanges ? (
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Assessment inputs changed</span>
      ) : null}
      {newCritical > 0 ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "4px 10px",
            borderRadius: 999,
            background: "color-mix(in srgb, var(--critical) 18%, transparent)",
            color: "var(--critical)",
          }}
        >
          +{newCritical} critical
        </span>
      ) : null}
      {newHigh > 0 ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "4px 10px",
            borderRadius: 999,
            background: "color-mix(in srgb, var(--warning) 18%, transparent)",
            color: "var(--warning)",
          }}
        >
          +{newHigh} warning
        </span>
      ) : null}
      {resolved > 0 ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "4px 10px",
            borderRadius: 999,
            background: "color-mix(in srgb, var(--healthy) 18%, transparent)",
            color: "var(--healthy)",
          }}
        >
          {resolved} resolved
        </span>
      ) : null}
      {hasItems
        ? items.slice(0, 4).map((it) => (
            <span key={`${it.kind}-${it.ruleId}`} style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {it.kind === "new" ? "●" : "✓"} {it.ruleId}
            </span>
          ))
        : null}
      {hasDriftChanges
        ? driftChanges.slice(0, 2).map((c) => (
            <span key={c.id || c.title} style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {String(c.severity || "").toLowerCase() === "critical" ? "●" : "○"} {c.title}
            </span>
          ))
        : null}
    </div>
  );
}

export default function ExecutiveDashboard({
  auditData,
  snapshots = [],
  priorSnapshot = null,
  selectedSnapshotId = null,
  driftData = null,
  providerId = "okta",
  isGreenfield = false,
}) {
  if (!auditData?.findings && !isGreenfield) return null;

  if (isGreenfield && !auditData?.findings) {
    const emptySeverity = buildSeverityCounts(null);
    const emptyCompliance = emptyComplianceRows(providerId);

    return (
      <section className="exec-dash">
        <div className="exec-dash-eyebrow">Executive dashboard</div>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 0, marginBottom: 16 }}>
          Fresh tenant — run an assessment to populate charts.
        </p>
        <DashboardHeroRow
          score={0}
          delta={null}
          hasPrior={false}
          scoreCaption={buildScoreCaption({ score: 0, openCriticalCount: 0 })}
          severity={emptySeverity}
          complianceRows={emptyCompliance}
          riskBySurface={EMPTY_RISK_SURFACES}
        />
      </section>
    );
  }

  const posture = computePostureScore(auditData);
  const score = posture.score;
  const scoreCaption =
    auditData?.summary?.oryCompleteness?.score_coverage?.line ||
    auditData?.summary?.scoreCoverage?.line ||
    buildScoreCaption({
      ...posture,
      scoreCoverageLine: auditData?.summary?.postureScoreMeta?.scoreCoverageLine,
    });
  const { delta, hasPrior } = computeScoreDelta(score, snapshots, priorSnapshot, selectedSnapshotId);

  const severity = buildSeverityCounts(auditData);
  const selected =
    auditData?.summary?.compliance?.selectedFrameworks ||
    getDefaultProviderComplianceFrameworks(providerId);
  const effective =
    auditData?.summary?.compliance?.effectiveFrameworks || selected;
  const complianceRows = buildComplianceByFramework(auditData, effective);
  const riskBySurface = buildRiskBySurface(auditData);
  const trendSeries = buildTrendSeries(snapshots, auditData);
  const driftSummary = priorSnapshot
    ? summarizeFindingDrift(priorSnapshot, auditData)
    : { newCritical: 0, newHigh: 0, resolved: 0, items: [] };

  return (
    <section className="exec-dash">
      <div className="exec-dash-eyebrow">Executive dashboard</div>

      <DashboardHeroRow
        score={score}
        delta={delta}
        hasPrior={hasPrior}
        scoreCaption={scoreCaption}
        severity={severity}
        complianceRows={complianceRows.length > 0 ? complianceRows : emptyComplianceRows(providerId)}
        riskBySurface={riskBySurface.length > 0 ? riskBySurface : EMPTY_RISK_SURFACES}
      />

      <ActionableSummaryPanel snapshot={auditData} providerId={providerId} variant="dashboard" />

      <PostureTrendChart trendSeries={trendSeries} snapshots={snapshots} />

      {auditData?.summary?.oryCompleteness ? (
        <OryCompletenessReport
          completeness={auditData.summary.oryCompleteness}
          normalizedFindings={auditData?.assessment?.findings}
        />
      ) : null}

      <div className="exec-dash-what-changed">
        <div className="exec-dash-what-changed__title">What changed</div>
        <WhatChangedStrip
          driftSummary={driftSummary}
          driftChanges={Array.isArray(driftData?.changes) ? driftData.changes : []}
          inputChangeSummary={
            Array.isArray(driftData?.input_change_summary)
              ? driftData.input_change_summary
              : Array.isArray(auditData?.input_change_summary)
                ? auditData.input_change_summary
                : Array.isArray(auditData?.summary?.input_change_summary)
                  ? auditData.summary.input_change_summary
                  : []
          }
          inputsChanged={Boolean(driftData?.inputs_changed || auditData?.inputs_changed || auditData?.summary?.inputs_changed)}
        />
      </div>
    </section>
  );
}
